
import EventManager from '@brainscape/event-manager';
import NewSearchBar from '_views/shared/NewSearchBar';
import React, { useState, useEffect, useRef } from 'react';
import searchSuggestion from '_models/searchSuggestion';
import TypeHelper from '_utils/TypeHelper';
import UiHelper from '_utils/UiHelper';
import UrlHelper from '_utils/UrlHelper';
import { useSyncedRef } from '_hooks/useSyncedRef';


const SearchBarController = ({
  domain = 'genome', // 'genome', 'crt', 'user-library'
  hasCloseButton = false,
  isMobileViewportSize,
  onCloseRequest,
  sidebarMode,
}) => {
  /*
  ==================================================
   INIT
  ==================================================
  */

  const eventManager = new EventManager();
  let _isMounted = false;


  /*
  ==================================================
   HOOKS
  ==================================================
  */

  const [query, setQuery] = useState('');
  const queryRef = useSyncedRef(query);

  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [suggestions, setSuggestions] = useState({});

  const debounceTimeout = useRef(null);
  const suggestionIndex = useRef({});


  // on Mount
  useEffect(() => {
    _isMounted = true;
    clearTimeoutsAndIntervals();
    subscribeToEvents();

    return () => {
      unsubscribeFromEvents();
      clearTimeoutsAndIntervals();
      _isMounted = false;
    };
  }, []);

  // debounce query input
  useEffect(() => {
    debounceTimeout.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300); // debounce delay in ms

    return () => {
      clearTimeout(debounceTimeout.current);
    };
  }, [query]);

  // Trigger upddate of suggestions when debouncedQuery updates
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setSuggestions({});
    } else {
      updateSuggestions(debouncedQuery);
    }
  }, [debouncedQuery]);


  /*
  ==================================================
   EVENT LISTENERS
  ==================================================
  */

  const subscribeToEvents = () => {
    eventManager.addListener('search-bar:close-request', handleCloseRequest);
    eventManager.addListener('search-bar:got-focus', handleGotFocus);
    eventManager.addListener('search-bar:query-change', handleQueryChange);
    eventManager.addListener('search-bar:search-request', handleSearchRequest);
  };

  const unsubscribeFromEvents = () => {
    eventManager.disable();
  };


  /*
  ==================================================
   EVENT HANDLERS
  ==================================================
  */

  const handleCloseRequest = () => {
    setQuery('');

    if (onCloseRequest) {
      onCloseRequest();
    }
  };

  const handleGotFocus = () => {
    getSuggestionIndex();
  };

  const handleQueryChange = (eventData) => {
    if (!eventData) return false;

    const queryText = eventData.queryText;
    if (typeof queryText !== 'string') return false;

    setQuery(queryText);
  };

  const handleSearchRequest = () => {
    const query = queryRef.current;

    if (!query || query == '') { return false; }

    // check to see if there is a certified class that matches the query
    const crtSuggestion = suggestionIndex.current.crt.find(suggestion => suggestion.name.toLowerCase() == query.toLowerCase());

    if (crtSuggestion?.path) {
      UiHelper.navigate(crtSuggestion.path);
      return true;
    }

    // check to see if there is a canned search that matches the query
    const tagSuggestion = suggestionIndex.current.tag.find(suggestion => suggestion.name.toLowerCase() == query.toLowerCase());

    if (tagSuggestion?.path) {
      UiHelper.navigate(tagSuggestion.path);
      return true;
    }

    // conduct a raw search
    const safeQuery = UrlHelper.safeEncodeUri(query, true);

    if (safeQuery) {
      UiHelper.navigate(`/subjects/${safeQuery}`);
    }
  };


  /*
  ==================================================
   LOCAL UTILS
  ==================================================
  */

  const clearTimeoutsAndIntervals = () => {
    clearTimeout(debounceTimeout.current);
  };

  const getSuggestionIndex = () => {
    if (!TypeHelper.isEmptyObject(suggestionIndex.current)) {
      return true;
    }

    searchSuggestion.index(domain)
      .then((rs) => {
        suggestionIndex.current = rs;
      })
      .catch((err) => {
        console.error('Error fetching suggestions: ', err);
        suggestionIndex.current = {};
      });
  };

  const updateSuggestions = (query) => {
    if (!query || query.length === 0) {
      setSuggestions({});
      return;
    }

    if (TypeHelper.isEmptyObject(suggestionIndex.current)) { return false; }

    const crtSuggestions = suggestionIndex.current.crt.filter(suggestion => suggestion.name.toLowerCase().includes(query.toLowerCase()));
    const tagSuggestions = suggestionIndex.current.tag.filter(suggestion => suggestion.name.toLowerCase().includes(query.toLowerCase()));

    setSuggestions({
      query: query,
      crt: crtSuggestions,
      tag: tagSuggestions,
      ugc: [],
    });
  }


  /*
  ==================================================
   EXPORTED COMPONENT
  ==================================================
  */

  return (
    <NewSearchBar
      domain={domain}
      hasCloseButton={hasCloseButton}
      isMobileViewportSize={isMobileViewportSize}
      query={query}
      suggestions={suggestions}
      sidebarMode={sidebarMode}
    />
  );
};

export default SearchBarController;
